import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import { ChevronLeft, Download } from 'lucide-react';

// Helper to join base URL and path
const joinUrl = (base, path) => {
  if (!base) return path;
  const b = base.endsWith('/') ? base.slice(0, -1) : base;
  const p = path.startsWith('/') ? path.slice(1) : path;
  return `${b}/${p}`;
};

export default function ViewPrescription() {
  const navigate = useNavigate();
  const location = useLocation();
  const prescriptionUrl = location?.state?.prescriptionUrl || '';
  const patientName = location?.state?.patientName || 'Prescription';

  const [pdfUrl, setPdfUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!prescriptionUrl) {
      setError('Prescription URL not found');
      setLoading(false);
      return;
    }

    let finalUrl = prescriptionUrl;

    if (
      !prescriptionUrl.startsWith('http://') &&
      !prescriptionUrl.startsWith('https://')
    ) {
      const IMG_BASEURL = process.env.REACT_APP_IMG_BASEURL || '';

      if (IMG_BASEURL) {
        if (prescriptionUrl.includes('curebay-prescription-docs')) {
          finalUrl = joinUrl(IMG_BASEURL, prescriptionUrl);
        } else {
          const basePath = joinUrl(IMG_BASEURL, 'curebay-prescription-docs');
          finalUrl = joinUrl(basePath, prescriptionUrl);
        }
      } else {
        if (prescriptionUrl.includes('curebay-prescription-docs')) {
          finalUrl = prescriptionUrl;
        } else {
          finalUrl = `curebay-prescription-docs/${prescriptionUrl}`;
        }
      }
    }

    setPdfUrl(finalUrl);
    setLoading(false);
  }, [prescriptionUrl]);

  const handleDownload = () => {
    if (!pdfUrl) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `${patientName}_Prescription.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto max-w-7xl px-4 py-6">
        <div className="mb-4 flex items-center justify-between rounded-lg bg-white p-4 shadow-sm">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="rounded-lg p-2 transition-colors hover:bg-gray-100"
              aria-label="Go back"
            >
              <ChevronLeft className="h-6 w-6 text-gray-700" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                View Prescription
              </h1>
              <p className="mt-1 text-sm text-gray-600">{patientName}</p>
            </div>
          </div>
          {pdfUrl ? (
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 rounded-lg bg-[#004171] px-4 py-2 text-white transition-colors hover:bg-[#003359]"
            >
              <Download className="h-4 w-4" />
              <span>Download</span>
            </button>
          ) : null}
        </div>

        <div className="overflow-hidden rounded-lg bg-white shadow-sm">
          {loading ? (
            <div className="flex h-[calc(100vh-250px)] items-center justify-center">
              <div className="text-center">
                <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-[#004171]" />
                <p className="text-gray-600">Loading prescription...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex h-[calc(100vh-250px)] items-center justify-center">
              <div className="text-center">
                <p className="mb-4 text-red-600">{error}</p>
                <button
                  onClick={() => navigate(-1)}
                  className="rounded-lg bg-gray-200 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-300"
                >
                  Go Back
                </button>
              </div>
            </div>
          ) : (
            <div className="h-[calc(100vh-250px)] w-full overflow-hidden">
              <object
                data={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                type="application/pdf"
                className="h-full w-full border-0"
                style={{ border: 'none' }}
              >
                <iframe
                  src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                  className="h-full w-full border-0"
                  title="Prescription PDF"
                  onError={() => {
                    setError('Failed to load prescription PDF');
                    setLoading(false);
                  }}
                  style={{ border: 'none' }}
                />
              </object>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
